import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  ConfigProvider,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  createBarna,
  deleteBarna,
  getBarna,
  listBarna,
  updateBarna,
  type Barna,
  type BhedSummary,
  type BarnaType,
  type SorakhariSummary,
} from '../api/barnaApi';
import {
  createBhed,
  deleteBhed,
  updateBhed,
  type AnunasikType,
  type CreateBhedBody,
  type MatraType,
  type SworType,
} from '../api/bhedApi';
import {
  createBhedBikalpa,
  deleteBhedBikalpa,
  updateBhedBikalpa,
  type BhedBikalpa,
} from '../api/bhedBikalpaApi';
import {
  createSorakhari,
  deleteSorakhari,
  getSorakhari,
  updateSorakhari,
  type SorakhariCombinationRow,
} from '../api/sorakhariApi';
import './BarnaPage.css';

type BarnaForm = { name: string; description: string; type: BarnaType };
type BhedForm = {
  matra: MatraType;
  swor: SworType;
  anunasik: AnunasikType;
  text: string;
};
type BikalpaRowForm = { bikalpa_order: number; text: string };
type SorakhariForm = {
  text: string;
  combination: SorakhariCombinationRow[];
};

function errMessage(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: { message?: string | string[] } }; message?: string };
  const msg = ax?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join('; ');
  if (typeof msg === 'string') return msg;
  return ax?.message || fallback;
}

/** Bhed (and bhed bikalpa) apply to swar and ayogabaha; vyanjan_sorakhari only for byanjan. */
function barnaAllowsBhed(t: BarnaType | undefined): boolean {
  return t === 'swar' || t === 'ayogabaha';
}

/** In vyanjan_sorakhari combination, bhed_id is required unless the row's barna is byanjan. */
function barnaRequiresBhedInCombination(t: BarnaType | undefined): boolean {
  return t === 'swar' || t === 'ayogabaha';
}

export function BarnaPage() {
  const [loading, setLoading] = useState(false);
  const [barnaRows, setBarnaRows] = useState<Barna[]>([]);
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(10);

  const [barnaModalOpen, setBarnaModalOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailBarna, setDetailBarna] = useState<Barna | null>(null);
  const [saving, setSaving] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [editingBarna, setEditingBarna] = useState<Barna | null>(null);
  const [bhedModalOpen, setBhedModalOpen] = useState(false);
  const [sorakhariModalOpen, setSorakhariModalOpen] = useState(false);
  const [editingBhed, setEditingBhed] = useState<BhedSummary | null>(null);
  const [editingSorakhari, setEditingSorakhari] = useState<SorakhariSummary | null>(null);
  const [modalSaving, setModalSaving] = useState(false);
  const [bikalpaTargetBhed, setBikalpaTargetBhed] = useState<BhedSummary | null>(null);
  const [bikalpaRowModalOpen, setBikalpaRowModalOpen] = useState(false);
  const [editingBikalpa, setEditingBikalpa] = useState<BhedBikalpa | null>(null);

  const [barnaForm] = Form.useForm<BarnaForm>();
  const [bhedForm] = Form.useForm<BhedForm>();
  const [sorakhariForm] = Form.useForm<SorakhariForm>();
  const [bikalpaRowForm] = Form.useForm<BikalpaRowForm>();
  const watchedCombinations = Form.useWatch('combination', sorakhariForm) as SorakhariCombinationRow[] | undefined;

  const barnaSelectOptions = barnaRows.map((row) => ({
    value: row.id,
    label: `${row.name} (#${row.id})`,
  }));
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const barna = await listBarna();
      setBarnaRows(barna);
    } catch (err: unknown) {
      message.error(errMessage(err, 'Failed to load barna data'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const refreshDetail = useCallback(async (barnaId: number): Promise<Barna | null> => {
    setDetailLoading(true);
    try {
      const full = await getBarna(barnaId);
      setDetailBarna(full);
      setBarnaRows((prev) => prev.map((b) => (b.id === full.id ? full : b)));
      return full;
    } catch (err: unknown) {
      message.error(errMessage(err, 'Failed to refresh barna detail'));
      return null;
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const openBarnaCreate = () => {
    setEditingBarna(null);
    barnaForm.setFieldsValue({ name: '', description: '', type: 'swar' });
    setBarnaModalOpen(true);
  };

  const onSaveBarna = async () => {
    try {
      const values = await barnaForm.validateFields();
      setSaving(true);
      if (editingBarna) {
        const patch: Partial<BarnaForm> = {};
        if (values.name !== editingBarna.name) patch.name = values.name;
        if (values.description !== editingBarna.description) patch.description = values.description;
        if (values.type !== editingBarna.type) patch.type = values.type;
        if (Object.keys(patch).length === 0) return message.info('No changes to save');
        await updateBarna(editingBarna.id, patch);
      } else {
        await createBarna(values);
      }
      setBarnaModalOpen(false);
      await loadAll();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err, 'Failed to save barna'));
    } finally {
      setSaving(false);
    }
  };

  const openCreateBhed = () => {
    if (!barnaAllowsBhed(detailBarna?.type)) {
      message.warning('Bhed can only be created for Barna types swar or ayogabaha.');
      return;
    }
    setEditingBhed(null);
    bhedForm.setFieldsValue({
      matra: 'hraswa',
      swor: 'udatta',
      anunasik: 'anunasik',
      text: '',
    });
    setBhedModalOpen(true);
  };

  const openEditBhed = (row: BhedSummary) => {
    if (!barnaAllowsBhed(detailBarna?.type)) {
      message.warning('Bhed can only be edited for Barna types swar or ayogabaha.');
      return;
    }
    setEditingBhed(row);
    bhedForm.setFieldsValue({
      matra: row.matra as MatraType,
      swor: row.swor as SworType,
      anunasik: row.anunasik,
      text: row.text,
    });
    setBhedModalOpen(true);
  };

  const saveBhed = async () => {
    if (!detailBarna) return;
    if (!barnaAllowsBhed(detailBarna.type)) {
      message.error('Bhed is allowed only for Barna types swar or ayogabaha.');
      return;
    }
    try {
      const values = await bhedForm.validateFields();
      setModalSaving(true);
      if (editingBhed) {
        const patch: Partial<CreateBhedBody> = {};
        if (values.matra !== editingBhed.matra) patch.matra = values.matra;
        if (values.swor !== editingBhed.swor) patch.swor = values.swor;
        if (values.anunasik !== editingBhed.anunasik) patch.anunasik = values.anunasik;
        if (values.text !== editingBhed.text) patch.text = values.text;
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          return;
        }
        await updateBhed(editingBhed.id, patch);
      } else {
        await createBhed({ ...values, barnaId: detailBarna.id });
      }
      setBhedModalOpen(false);
      await refreshDetail(detailBarna.id);
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err, 'Failed to save bhed'));
    } finally {
      setModalSaving(false);
    }
  };

  const removeBhed = async (id: number) => {
    if (!detailBarna) return;
    try {
      await deleteBhed(id);
      await refreshDetail(detailBarna.id);
    } catch (err: unknown) {
      message.error(errMessage(err, 'Failed to delete bhed'));
    }
  };

  const openCreateSorakhari = () => {
    if (!detailBarna) return;
    if (detailBarna.type !== 'byanjan') {
      message.warning('Vyanjan Sorakhari can only be created for Barna type byanjan.');
      return;
    }
    setEditingSorakhari(null);
    sorakhariForm.setFieldsValue({
      text: '',
      combination: [{ barna_id: detailBarna.id, bhed_id: null, order: 0 }],
    });
    setSorakhariModalOpen(true);
  };

  const openEditSorakhari = async (row: SorakhariSummary) => {
    if (detailBarna?.type !== 'byanjan') {
      message.warning('Vyanjan Sorakhari can only be edited for Barna type byanjan.');
      return;
    }
    try {
      const full = await getSorakhari(row.id);
      setEditingSorakhari(row);
      sorakhariForm.setFieldsValue({
        text: full.text,
        combination: full.combination,
      });
      setSorakhariModalOpen(true);
    } catch (err: unknown) {
      message.error(errMessage(err, 'Failed to load sorakhari for edit'));
    }
  };

  const saveSorakhari = async () => {
    if (!detailBarna) return;
    if (detailBarna.type !== 'byanjan') {
      message.error('Vyanjan Sorakhari is allowed only for Barna type byanjan.');
      return;
    }
    try {
      const values = await sorakhariForm.validateFields();
      const hasMissingBhed = values.combination.some((row) => {
        const rowBarna = barnaRows.find((b) => b.id === Number(row.barna_id));
        return barnaRequiresBhedInCombination(rowBarna?.type) && row.bhed_id == null;
      });
      if (hasMissingBhed) {
        message.error('bhed_id is required when selected barna is swar or ayogabaha.');
        return;
      }
      const combination = values.combination.map((row) => ({
        barna_id: Number(row.barna_id),
        bhed_id: row.bhed_id == null ? null : Number(row.bhed_id),
        order: Number(row.order),
      }));
      setModalSaving(true);
      if (editingSorakhari) {
        await updateSorakhari(editingSorakhari.id, { text: values.text, combination, barnaId: detailBarna.id });
      } else {
        await createSorakhari({ text: values.text, combination, barnaId: detailBarna.id });
      }
      setSorakhariModalOpen(false);
      await refreshDetail(detailBarna.id);
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err, 'Failed to save sorakhari'));
    } finally {
      setModalSaving(false);
    }
  };

  const removeSorakhari = async (id: number) => {
    if (!detailBarna) return;
    try {
      await deleteSorakhari(id);
      await refreshDetail(detailBarna.id);
    } catch (err: unknown) {
      message.error(errMessage(err, 'Failed to delete sorakhari'));
    }
  };

  const openBikalpaCreateForParent = (parent: BhedSummary) => {
    setBikalpaTargetBhed(parent);
    setEditingBikalpa(null);
    const nextOrder = parent.bikalpas?.length ?? 0;
    bikalpaRowForm.setFieldsValue({ bikalpa_order: nextOrder, text: '' });
    setBikalpaRowModalOpen(true);
  };

  const openEditBikalpaRow = (parentBhed: BhedSummary, row: BhedBikalpa) => {
    setBikalpaTargetBhed(parentBhed);
    setEditingBikalpa(row);
    bikalpaRowForm.setFieldsValue({ bikalpa_order: row.bikalpaOrder, text: row.text });
    setBikalpaRowModalOpen(true);
  };

  const saveBikalpaRow = async () => {
    if (!detailBarna || !bikalpaTargetBhed) return;
    try {
      const values = await bikalpaRowForm.validateFields();
      setModalSaving(true);
      if (editingBikalpa) {
        const patch: { bikalpa_order?: number; text?: string } = {};
        if (values.bikalpa_order !== editingBikalpa.bikalpaOrder) patch.bikalpa_order = values.bikalpa_order;
        if (values.text !== editingBikalpa.text) patch.text = values.text;
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          setBikalpaRowModalOpen(false);
          return;
        }
        await updateBhedBikalpa(editingBikalpa.id, patch);
      } else {
        await createBhedBikalpa({
          bhedId: bikalpaTargetBhed.id,
          bikalpa_order: values.bikalpa_order,
          text: values.text,
        });
      }
      const full = await refreshDetail(detailBarna.id);
      const nextBhed = full?.bheds?.find((b) => b.id === bikalpaTargetBhed.id);
      if (nextBhed) setBikalpaTargetBhed(nextBhed);
      setBikalpaRowModalOpen(false);
      setEditingBikalpa(null);
      bikalpaRowForm.resetFields();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err, 'Failed to save bhed bikalpa'));
    } finally {
      setModalSaving(false);
    }
  };

  const removeBikalpaRow = async (_parentBhed: BhedSummary, id: number) => {
    if (!detailBarna) return;
    try {
      await deleteBhedBikalpa(id);
      await refreshDetail(detailBarna.id);
    } catch (err: unknown) {
      message.error(errMessage(err, 'Failed to delete bhed bikalpa'));
    }
  };

  const getBikalpaNestedColumns = (parentBhed: BhedSummary): ColumnsType<BhedBikalpa> => [
    { title: 'ID', dataIndex: 'id', width: 72 },
    { title: 'Order', dataIndex: 'bikalpaOrder', width: 88 },
    { title: 'Text', dataIndex: 'text', ellipsis: true },
    {
      title: 'Actions',
      width: 160,
      render: (_, r) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => openEditBikalpaRow(parentBhed, r)}>
            Edit
          </Button>
          <Popconfirm title="Delete this bhed bikalpa row?" onConfirm={() => void removeBikalpaRow(parentBhed, r.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const bhedDetailColumns: ColumnsType<BhedSummary> = [
    { title: 'ID', dataIndex: 'id', width: 72 },
    { title: 'Text', dataIndex: 'text' },
    { title: 'Matra', dataIndex: 'matra', width: 100 },
    { title: 'Swor', dataIndex: 'swor', width: 100 },
    { title: 'Anunasik', dataIndex: 'anunasik', width: 120 },
    {
      title: 'Bikalpa rows',
      width: 110,
      render: (_, row) => row.bikalpas?.length ?? 0,
    },
    {
      title: 'Actions',
      width: 180,
      render: (_, row) => (
        <Space size="small" wrap>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditBhed(row)}
            disabled={!barnaAllowsBhed(detailBarna?.type)}
          >
            Edit
          </Button>
          <Popconfirm title="Delete this bhed?" onConfirm={() => void removeBhed(row.id)}>
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={!barnaAllowsBhed(detailBarna?.type)}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const sorakhariDetailColumns: ColumnsType<SorakhariSummary> = [
    { title: 'ID', dataIndex: 'id', width: 72 },
    { title: 'Text', dataIndex: 'text' },
    {
      title: 'Combination',
      render: (_, row) => {
        const parts = [...(row.combination ?? [])]
          .sort((a, b) => a.order - b.order)
          .map((item) => {
            const sourceBarna = barnaRows.find((b) => b.id === item.barna_id);
            if (item.bhed_id == null) return sourceBarna?.name || `#${item.barna_id}`;
            const sourceBhed = sourceBarna?.bheds?.find((b) => b.id === item.bhed_id);
            return sourceBhed?.text || `#${item.bhed_id}`;
          });
        return parts.length > 0 ? parts.join(' + ') : '—';
      },
    },
    {
      title: 'Actions',
      width: 180,
      render: (_, row) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => void openEditSorakhari(row)}
            disabled={detailBarna?.type !== 'byanjan'}
          >
            Edit
          </Button>
          <Popconfirm title="Delete this sorakhari?" onConfirm={() => void removeSorakhari(row.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const barnaColumns: ColumnsType<Barna> = [
    { title: 'ID', dataIndex: 'id', width: 72 },
    { title: 'Name', dataIndex: 'name' },
    {
      title: 'Type',
      dataIndex: 'type',
      render: (t: BarnaType) => (
        <Tag color={t === 'swar' ? 'blue' : t === 'byanjan' ? 'purple' : 'green'}>{t}</Tag>
      ),
    },
    { title: 'Description', dataIndex: 'description', ellipsis: true },
    { title: 'Bheds', render: (_, r) => r.bheds?.length ?? 0, width: 90 },
    { title: 'Vyanjan Sorakhari', render: (_, r) => r.vyanjanSorakhari?.length ?? 0, width: 160 },
    {
      title: 'Actions',
      width: 240,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={async () => {
              try {
                setDetailLoading(true);
                setDetailOpen(true);
                const full = await getBarna(record.id);
                setDetailBarna(full);
              } catch (err: unknown) {
                setDetailOpen(false);
                message.error(errMessage(err, 'Failed to load barna detail'));
              } finally {
                setDetailLoading(false);
              }
            }}
          >
            View
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingBarna(record);
              barnaForm.setFieldsValue({ name: record.name, description: record.description, type: record.type });
              setBarnaModalOpen(true);
            }}
          >
            Edit
          </Button>
          <Popconfirm title="Delete this barna?" onConfirm={() => void deleteBarna(record.id).then(loadAll)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <ConfigProvider theme={{ token: { fontFamily: "'Knswami', sans-serif" } }}>
      <div className="barna-page-root" style={{ width: '100%' }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Card
        title="Barna"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openBarnaCreate}>
            Add Barna
          </Button>
        }
      >
        <Table
          rowKey="id"
          loading={loading}
          columns={barnaColumns}
          dataSource={barnaRows}
          pagination={{
            current: listPage,
            pageSize: listPageSize,
            total: barnaRows.length,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total, [from, to]) =>
              total > 0 ? `${from}-${to} of ${total} barnas` : '0 barnas',
            onChange: (page, pageSize) => {
              const size = pageSize ?? listPageSize;
              if (size !== listPageSize) {
                setListPageSize(size);
                setListPage(1);
              } else {
                setListPage(page);
              }
            },
          }}
        />
      </Card>

      <Modal title={editingBarna ? 'Edit Barna' : 'Add Barna'} open={barnaModalOpen} onCancel={() => setBarnaModalOpen(false)} onOk={() => void onSaveBarna()} confirmLoading={saving}>
        <Form layout="vertical" form={barnaForm}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true, message: 'Description is required' }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="type" label="Type" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'swar', label: 'swar' },
                { value: 'byanjan', label: 'byanjan' },
                { value: 'ayogabaha', label: 'ayogabaha' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={detailBarna ? `Barna #${detailBarna.id}` : 'Barna details'}
        open={detailOpen}
        onCancel={() => {
          setDetailOpen(false);
          setDetailBarna(null);
        }}
        footer={null}
        width={900}
      >
        {detailLoading ? (
          <Typography.Text type="secondary">Loading…</Typography.Text>
        ) : detailBarna ? (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Space direction="vertical" size={0}>
              <Typography.Text>
                <strong>Name:</strong> {detailBarna.name}
              </Typography.Text>
              <Typography.Text>
                <strong>Type:</strong> {detailBarna.type}
              </Typography.Text>
              <Typography.Text>
                <strong>Description:</strong> {detailBarna.description}
              </Typography.Text>
            </Space>

            <Card title={`Bhed (${detailBarna.bheds?.length ?? 0})`} size="small">
              <div style={{ marginBottom: 8 }}>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={openCreateBhed}
                  disabled={!barnaAllowsBhed(detailBarna.type)}
                >
                  Add Bhed
                </Button>
              </div>
              <Table<BhedSummary>
                rowKey="id"
                columns={bhedDetailColumns}
                dataSource={detailBarna.bheds ?? []}
                pagination={false}
                size="small"
                locale={{ emptyText: 'No bhed rows' }}
                scroll={{ x: 860 }}
                expandable={{
                  rowExpandable: () => barnaAllowsBhed(detailBarna.type),
                  expandedRowRender: (record) => (
                    <div style={{ margin: '4px 0 12px 48px' }}>
                      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                        Bikalpa variants ({record.bikalpas?.length ?? 0})
                      </Typography.Text>
                      <Table<BhedBikalpa>
                        rowKey="id"
                        size="small"
                        pagination={false}
                        columns={getBikalpaNestedColumns(record)}
                        dataSource={[...(record.bikalpas ?? [])].sort((a, b) => a.bikalpaOrder - b.bikalpaOrder)}
                        locale={{ emptyText: 'No rows' }}
                        scroll={{ x: 560 }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                        <Button
                          type="primary"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => openBikalpaCreateForParent(record)}
                        >
                          Add bikalpa
                        </Button>
                      </div>
                    </div>
                  ),
                }}
              />
            </Card>

            <Card title={`Vyanjan Sorakhari (${detailBarna.vyanjanSorakhari?.length ?? 0})`} size="small">
              <div style={{ marginBottom: 8 }}>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={openCreateSorakhari}
                  disabled={detailBarna.type !== 'byanjan'}
                >
                  Add Sorakhari
                </Button>
              </div>
              <Table<SorakhariSummary>
                rowKey="id"
                columns={sorakhariDetailColumns}
                dataSource={detailBarna.vyanjanSorakhari ?? []}
                pagination={false}
                size="small"
                locale={{ emptyText: 'No sorakhari rows' }}
              />
            </Card>
          </Space>
        ) : null}
      </Modal>

      <Modal
        title={editingBhed ? 'Edit Bhed' : 'Add Bhed'}
        open={bhedModalOpen}
        onCancel={() => setBhedModalOpen(false)}
        onOk={() => void saveBhed()}
        confirmLoading={modalSaving}
      >
        <Form layout="vertical" form={bhedForm}>
          <Form.Item name="matra" label="Matra" rules={[{ required: true }]}>
            <Select options={[{ value: 'hraswa' }, { value: 'dirgha' }, { value: 'pluta' }]} />
          </Form.Item>
          <Form.Item name="swor" label="Swor" rules={[{ required: true }]}>
            <Select options={[{ value: 'udatta' }, { value: 'anudatta' }, { value: 'swarit' }]} />
          </Form.Item>
          <Form.Item name="anunasik" label="Anunasik" rules={[{ required: true }]}>
            <Select options={[{ value: 'anunasik' }, { value: 'ananunasik' }]} />
          </Form.Item>
          <Form.Item name="text" label="Text" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingBikalpa ? 'Edit bhed bikalpa' : 'Add bhed bikalpa'}
        open={bikalpaRowModalOpen}
        onCancel={() => {
          setBikalpaRowModalOpen(false);
          setEditingBikalpa(null);
          bikalpaRowForm.resetFields();
        }}
        onOk={() => void saveBikalpaRow()}
        confirmLoading={modalSaving}
      >
        <Form layout="vertical" form={bikalpaRowForm}>
          <Form.Item name="bikalpa_order" label="bikalpa_order" rules={[{ required: true, message: 'Order is required' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="text" label="text">
            <Input placeholder="Optional variant label" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingSorakhari ? 'Edit Sorakhari' : 'Add Sorakhari'}
        open={sorakhariModalOpen}
        onCancel={() => {
          setSorakhariModalOpen(false);
          sorakhariForm.resetFields();
        }}
        onOk={() => void saveSorakhari()}
        confirmLoading={modalSaving}
      >
        <Form layout="vertical" form={sorakhariForm}>
          <Form.Item name="text" label="Text" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.List name="combination" rules={[{ validator: async (_, value) => {
            if (!value || value.length === 0) {
              throw new Error('At least one combination row is required');
            }
          } }]}>
            {(fields, { add, remove }, { errors }) => (
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <Typography.Text strong>Combination rows</Typography.Text>
                {fields.map((field) => (
                  <Space key={field.key} align="start" wrap>
                    {(() => {
                      const selectedBarnaId = Number(watchedCombinations?.[field.name]?.barna_id);
                      const rowBarna = barnaRows.find((row) => row.id === selectedBarnaId);
                      const requiresBhed = barnaRequiresBhedInCombination(rowBarna?.type);
                      const rowBhedOptions = (rowBarna?.bheds ?? []).map((row) => ({
                        value: row.id,
                        label: `${row.text || '-'} (#${row.id})`,
                      }));
                      return (
                        <>
                    <Form.Item
                      {...field}
                      label="barna_id"
                      name={[field.name, 'barna_id']}
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Select
                        showSearch
                        options={barnaSelectOptions}
                        placeholder="Select barna"
                        onChange={() => {
                          sorakhariForm.setFieldValue(['combination', field.name, 'bhed_id'], undefined);
                        }}
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          String(option?.label ?? '')
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        style={{ width: 220 }}
                      />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      label="bhed_id"
                      name={[field.name, 'bhed_id']}
                      rules={[
                        {
                          validator: async (_, value) => {
                            if (!requiresBhed) return;
                            if (value == null || value === '') throw new Error('Required for swar or ayogabaha');
                          },
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        options={rowBhedOptions}
                        placeholder={requiresBhed ? 'Select bhed' : 'Not required for byanjan'}
                        disabled={!requiresBhed}
                        allowClear
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          String(option?.label ?? '')
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        style={{ width: 220 }}
                      />
                    </Form.Item>
                        </>
                      );
                    })()}
                    <Form.Item
                      {...field}
                      label="order"
                      name={[field.name, 'order']}
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <InputNumber min={0} />
                    </Form.Item>
                    <Button
                      type="text"
                      danger
                      icon={<MinusCircleOutlined />}
                      onClick={() => remove(field.name)}
                      style={{ marginTop: 30 }}
                    />
                  </Space>
                ))}
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => add({ barna_id: detailBarna?.id ?? 1, bhed_id: null, order: fields.length })}
                >
                  Add combination row
                </Button>
                <Form.ErrorList errors={errors} />
              </Space>
            )}
          </Form.List>
        </Form>
      </Modal>
        </Space>
      </div>
    </ConfigProvider>
  );
}
